import React, { useMemo } from "react";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {
  deriveBanner,
  type DerivableCallout,
  type SummaryTupleScope,
} from "./summaryDerive";
import type { KpiCell, KeyCalloutRef } from "../../models/API";

const ACCENT_ORANGE = "#FF9900";

// Matches a signed metric token, e.g. "+0.6%", "-33.2%", "+$25MM", "−5 bps".
const METRIC_TOKEN_RE = /([+\-\u2212]\s?\$?\d[\d.,]*\s*(?:%|bps|pp|MM|K|B)?)/g;

/**
 * Color signed metric tokens in a plain-text string: positive (leading "+")
 * green via `.pos`, negative (leading "-"/"−") red via `.neg`. Returns React
 * nodes (never raw HTML), so it is safe for user-edited KPI sub-text.
 */
function colorizeMetrics(text: string): React.ReactNode {
  if (!text) return text;
  const out: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(METRIC_TOKEN_RE);
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    const neg = /^[\s]*[-\u2212]/.test(token);
    out.push(
      <span key={key++} className={neg ? "neg" : "pos"}>
        {token}
      </span>
    );
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
}

/**
 * Colorize signed metric tokens inside an HTML string (for the overview prose,
 * which renders via dangerouslySetInnerHTML). Only text *outside* existing
 * spans is wrapped, so content that already carries `.pos`/`.neg`/`.neutral`
 * spans (e.g. regenerated narratives) is left untouched — making this safe to
 * run repeatedly. Tag internals are never touched (we only transform text
 * segments between tags), so no attributes/markup can be corrupted.
 */
function colorizeMetricsHtml(html: string): string {
  if (!html) return html;
  let spanDepth = 0;
  return html
    .split(/(<[^>]+>)/g)
    .map((seg) => {
      if (seg.startsWith("<")) {
        if (/^<span\b/i.test(seg)) spanDepth += 1;
        else if (/^<\/span>/i.test(seg)) spanDepth = Math.max(0, spanDepth - 1);
        return seg;
      }
      if (spanDepth > 0) return seg; // already inside a colored span
      return seg.replace(METRIC_TOKEN_RE, (tok) => {
        const neg = /^\s*[-\u2212]/.test(tok);
        return `<span class="${neg ? "neg" : "pos"}">${tok}</span>`;
      });
    })
    .join("");
}

/**
 * Resolve key_callouts references against the callouts list.
 * Stale references (deleted callout or no match) are silently dropped (FR-4.4).
 */
function resolveKeyCallouts(
  refs: KeyCalloutRef[],
  callouts: ReadonlyArray<DerivableCallout>
): Array<{ ref: KeyCalloutRef; callout: DerivableCallout }> {
  return refs
    .map((ref) => {
      const match = callouts.find(
        (c) =>
          c.page_num === ref.page_num &&
          c.card_number === ref.card_number &&
          !(
            (c as any).deleted_at !== undefined &&
            (c as any).deleted_at !== null &&
            String((c as any).deleted_at).trim() !== ""
          )
      );
      return match ? { ref, callout: match } : null;
    })
    .filter(Boolean) as Array<{
    ref: KeyCalloutRef;
    callout: DerivableCallout;
  }>;
}

interface SummarySectionProps {
  /** Style blocks (calloutStyles + the viewer's CSS overrides) to inject. */
  styles: string;
  /** The unified callout list scoped to the displayed report/tuple. */
  callouts: ReadonlyArray<DerivableCallout> | null | undefined;
  /** Optional (team_id, week, report_name) scope for the derived parts. */
  scope?: SummaryTupleScope;
  /** KPI strip cells from the DB (structured, replaces old summaryContent). */
  kpiStrip: KpiCell[];
  /** Overview narrative prose from the DB. */
  overview: string | null;
  /** Key callout references from the DB — resolved at render time against callouts. */
  keyCallouts: KeyCalloutRef[];
  /**
   * Legacy executive-summary HTML (the `content` column). Rendered as a
   * fallback when the structured columns (overview / kpi_strip / key_callouts)
   * are all empty — e.g. rows produced before the structured store was
   * populated. Part of the coexistence period (content is not dropped yet).
   */
  legacyContent?: string | null;
  /** True while the structured summary read is in flight. */
  summaryLoading: boolean;
  /**
   * Whether the current user may edit the summary for this tuple
   * (same-team or FINN cross-team — see canEditCallout in the viewer).
   */
  canEdit: boolean;
  /** True while the Summary_Editor is already open (disables the edit control). */
  editorOpen: boolean;
  /** Invoked when the user activates the edit control. */
  onEditClick: () => void;
}

/**
 * SummarySection — renders the decomposed WBR callout summary section:
 *
 *   1. Summary_Banner   — derived, read-only, exactly FOUR cells
 *      (Total Callouts, Positive Signals, Large Callouts, Manual Callouts).
 *   2. KPI Strip        — from DB `kpi_strip` structured column.
 *   3. Overview         — from DB `overview` TEXT column, rendered via dangerouslySetInnerHTML
 *      (small scope — inline tags only).
 *   4. Key_Callouts_Table — resolved from DB `key_callouts` refs against callouts list.
 *
 * The edit icon is shown when `overview !== null` (i.e., a row exists in the DB).
 */
const SummarySection: React.FC<SummarySectionProps> = ({
  styles,
  callouts,
  scope,
  kpiStrip,
  overview,
  keyCallouts,
  legacyContent,
  summaryLoading,
  canEdit,
  editorOpen,
  onEditClick,
}) => {
  const banner = useMemo(
    () => deriveBanner(callouts, scope),
    [callouts, scope]
  );

  const resolvedKeyCallouts = useMemo(
    () =>
      callouts && keyCallouts.length > 0
        ? resolveKeyCallouts(keyCallouts, callouts)
        : [],
    [callouts, keyCallouts]
  );

  // Whether the structured columns carry any renderable content.
  const hasStructured =
    (overview != null && overview.trim() !== "") ||
    kpiStrip.length > 0 ||
    keyCallouts.length > 0;

  // Legacy executive-summary HTML to fall back on when no structured content.
  const hasLegacy = legacyContent != null && legacyContent.trim() !== "";

  // A summary row exists (and is thus renderable) when the structured overview
  // is non-null OR a legacy content blob is present.
  const hasSummaryRow = overview !== null || hasLegacy;

  // Show edit control when a row exists in the DB and user has permission.
  const showEditControl = canEdit && hasSummaryRow;

  return (
    <Box>
      {/* Inject the callout styles + viewer overrides so the derived banner,
          table, and the structured sections all pick up the same look. */}
      {styles && <div dangerouslySetInnerHTML={{ __html: styles }} />}

      {/* ── 1. Summary_Banner (derived, read-only, exactly four cells) ── */}
      <div className="summary-banner">
        <div className="summary-cell">
          <div className="summary-cell-label">TOTAL CALLOUTS</div>
          <div className="summary-cell-value orange">{banner.total}</div>
          <div className="summary-cell-sub">
            {banner.total === 1 ? "1 callout" : `${banner.total} callouts`}
          </div>
        </div>
        <div className="summary-cell">
          <div className="summary-cell-label">POSITIVE SIGNALS</div>
          <div className="summary-cell-value green">{banner.positive}</div>
          <div className="summary-cell-sub">
            {banner.watch === 1
              ? "1 watch item"
              : `${banner.watch} watch items`}
          </div>
        </div>
        <div className="summary-cell">
          <div className="summary-cell-label">LARGE CALLOUTS</div>
          <div className="summary-cell-value ink">{banner.large}</div>
          <div className="summary-cell-sub">
            {banner.medium} medium, {banner.small} small
          </div>
        </div>
        <div className="summary-cell">
          <div className="summary-cell-label">MANUAL CALLOUTS</div>
          <div className="summary-cell-value blue">{banner.manual}</div>
          <div className="summary-cell-sub">
            {banner.ai === 1 ? "1 AI-generated" : `${banner.ai} AI-generated`}
          </div>
        </div>
      </div>

      {/* ── 2 + 3 + 4. Executive summary: KPI Strip, Overview, Key Callouts ── */}
      <div className="exec-summary">
        {summaryLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 3,
            }}
          >
            <CircularProgress size={24} sx={{ color: ACCENT_ORANGE }} />
          </Box>
        ) : hasSummaryRow ? (
          <Box sx={{ position: "relative" }}>
            {showEditControl && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  zIndex: 2,
                }}
              >
                <Tooltip
                  title={editorOpen ? "Editor already open" : "Edit summary"}
                >
                  {/* span wrapper so the tooltip works while the button is disabled */}
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Edit summary"
                      onClick={onEditClick}
                      disabled={editorOpen}
                      sx={{ color: "#1B3A6B" }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}

            {/* Structured columns when present; otherwise fall back to the
                legacy executive-summary HTML (coexistence period). */}
            {hasStructured ? (
              <>
                {/* ── KPI Strip (from DB kpi_strip structured column) ── */}
                {kpiStrip.length > 0 && (
                  <div className="kpi-strip">
                    {kpiStrip.map((cell, idx) => (
                      <div className="kpi-cell" key={idx}>
                        <div className="kpi-label">{cell.label}</div>
                        <div
                          className={`kpi-value${
                            cell.color ? ` ${cell.color}` : ""
                          }`}
                        >
                          {cell.value}
                        </div>
                        <div className="kpi-delta">
                          {colorizeMetrics(cell.sub)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Overview narrative (from DB overview TEXT column) ── */}
                {overview && (
                  <>
                    <div className="exec-section-header">Overview</div>
                    <div
                      className="narrative"
                      dangerouslySetInnerHTML={{
                        __html: colorizeMetricsHtml(overview),
                      }}
                    />
                  </>
                )}

                {/* ── Key Callouts Table (resolved from key_callouts refs) ── */}
                {resolvedKeyCallouts.length > 0 && (
                  <>
                    <div className="exec-section-header">Key Callouts</div>
                    <table className="callout-table">
                      <thead>
                        <tr>
                          <th style={{ width: "72px" }}>Pg : #</th>
                          <th style={{ width: "240px" }}>Callout</th>
                          <th>Summary &amp; Key Metrics</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resolvedKeyCallouts.map(({ ref, callout }) => {
                          const numClass =
                            callout.status === "positive"
                              ? "pos-num"
                              : "neg-num";
                          return (
                            <tr
                              key={`${ref.page_num}-${ref.card_number}`}
                              data-callout-id={callout.id}
                            >
                              <td>
                                <span className="callout-ref">
                                  <span className="callout-ref-page">
                                    p{callout.page_num}:
                                  </span>
                                  <span className={`callout-num ${numClass}`}>
                                    {callout.card_number &&
                                    callout.card_number > 0
                                      ? callout.card_number
                                      : "—"}
                                  </span>
                                </span>
                              </td>
                              <td>
                                <div className="callout-title-text">
                                  {callout.title}
                                </div>
                              </td>
                              <td>{colorizeMetrics(callout.why_text ?? "")}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )}
              </>
            ) : hasLegacy ? (
              <div
                dangerouslySetInnerHTML={{ __html: legacyContent as string }}
              />
            ) : null}
          </Box>
        ) : null}
      </div>
    </Box>
  );
};

export default SummarySection;
